import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { MainLayout } from '../../components/MainLayout';
import { threadsService } from '../../services/threads.service';
import { agentsService } from '../../services/agents.service';
import { useAuth } from '../../hooks/useAuth';
import { useScopeContext } from '../../contexts/ScopeContext';
import { getAuthHeaders } from '../../utils/auth';
import type { ListThreadsOutput } from '@zenmako/contracts/threads';

export function AgentThreadsPage({ agentId }: { agentId: string }) {
  const { user } = useAuth();
  const scope = useScopeContext();
  const [threads, setThreads] = useState<ListThreadsOutput['threads']>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agent, setAgent] = useState<any>(null);

  useEffect(() => {
    loadAgent();
    loadThreads();
  }, [agentId, scope.teamId]);

  const loadAgent = async () => {
    try {
      const agentData = await agentsService.getAgent(scope.teamId, agentId);
      setAgent(agentData);
    } catch (err) {
      console.error('Failed to load agent:', err);
    }
  };

  const loadThreads = async () => {
    if (!user) return;
    try {
      setLoading(true);

      // Use team-based resourceId instead of user-based
      const resourceId = `t#${scope.teamId}`;
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/threads?resourceId=${encodeURIComponent(resourceId)}&agentId=${encodeURIComponent(agentId)}&page=0&perPage=100&orderBy=updatedAt&sortDirection=DESC`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to load threads`);
      }

      const result = await response.json();

      // Backend now filters by agentId, so we can use the threads directly
      const agentThreads: ListThreadsOutput['threads'] = (result.threads ? result.threads : [])
        .map((thread: any) => ({
          ...thread,
          threadId: thread.threadId,
          updatedAt: thread.lastActivity ? thread.lastActivity : thread.createdAt
        }));
      setThreads(agentThreads);
    } catch (err) {
      console.error('Failed to load threads:', err);
      setError(err instanceof Error ? err.message : 'Failed to load threads');
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    if (!user) return;
    // Generate thread ID with format: agentId-guid-timestamp
    const guid = crypto.randomUUID ? crypto.randomUUID() :
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    const timestamp = Date.now();
    const threadId = `${agentId}-${guid}-${timestamp}`;

    // Mastra will automatically create and save the thread when we navigate to it
    route(`${scope.getNavigationPrefix()}/chat/${agentId}/${threadId}`);
  };

  const handleDeleteThread = async (threadId: string) => {
    if (!confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await threadsService.deleteThread(scope.teamId, threadId);
      await loadThreads();
    } catch (err) {
      console.error('Failed to delete thread:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete thread');
    }
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes === 0 ? 'Just now' : `${minutes}m ago`;
      }
      return `${hours}h ago`;
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return d.toLocaleDateString();
    }
  };

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">

      <div class="container mx-auto p-8 max-w-4xl">
        {/* Header */}
        <div class="flex justify-between items-center mb-6">
          <div>
            <h1 class="text-3xl font-bold">
              {agent?.name || agentId} Conversations
            </h1>
            {agent?.description && (
              <p class="text-base-content/70 mt-2">{agent.description}</p>
            )}
          </div>
          <button
            onClick={handleNewChat}
            class="btn btn-primary"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div class="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div class="flex justify-center py-12">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
        )}

        {/* Threads List */}
        {!loading && threads && threads.length === 0 && (
          <div class="card bg-base-200">
            <div class="card-body text-center py-12">
              <h2 class="text-xl font-semibold mb-2">No conversations yet</h2>
              <p class="text-base-content/70 mb-4">
                Start a new conversation with {agent?.name || agentId}
              </p>
              <div>
                <button
                  onClick={handleNewChat}
                  class="btn btn-primary"
                >
                  Start Your First Chat
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && threads && threads.length > 0 && (
          <div class="space-y-3">
            {threads.map((thread) => (
              <div
                key={thread.threadId}
                class="card bg-base-200 hover:bg-base-300 transition-colors cursor-pointer"
                onClick={() => {
                  route(`${scope.getNavigationPrefix()}/chat/${agentId}/${thread.threadId}`);
                }}
              >
                <div class="card-body p-4">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <h3 class="font-semibold">
                        {thread.title || 'Untitled Conversation'}
                      </h3>
                      {thread.lastMessage && (
                        <p class="text-sm text-base-content/70 mt-1 line-clamp-2">
                          {thread.lastMessage}
                        </p>
                      )}
                      <p class="text-xs text-base-content/50 mt-2">
                        {formatDate(thread.lastActivity || thread.createdAt)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteThread(thread.threadId);
                      }}
                      class="btn btn-ghost btn-sm btn-square"
                      aria-label="Delete thread"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}    </div>
      </div>
    </MainLayout>
  );
}
